import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCreativeJobsMap, upsertJob } from '@/store/slices/creativeJobs';
import { creativesServiceConfigured } from './creative-api';
import {
  SITE_REDRAW_KIND,
  SITE_REDRAW_PLACEMENT,
  applyRefusal,
  applySiteRedraw,
  atfHeroOf,
  cardRedrawOffers,
  discardRefusal,
  discardSiteRedraw,
  fetchSiteArt,
  fetchSiteRedraw,
  isPendingRedraw,
  redrawRefusal,
  requestSiteRedraw,
  type CardRedrawOffer,
  type SiteArtRead,
  type SiteRedraw,
} from './above-the-fold-api';

/** The request out for a card image: a redraw asked for one image, or the waiting candidate applied or thrown away. */
export type CardRedrawBusy = { action: 'redraw'; fieldName: string } | { action: 'apply' } | { action: 'discard' } | null;

export interface AtfCardRedrawsState {
  /** The hero's images by name (the card tiles' names) with what each offers; null while the art is unread, or where
   *  no redraw is offered (no hero, no price, the art could not be read). */
  offers: Record<string, CardRedrawOffer> | null;
  /** What one redraw costs as spark-creatives prices it (undefined when it is not priced). */
  price?: number;
  /** The hero's redraw being drawn or waiting to be applied (one per deployment), if any. */
  pending: SiteRedraw | null;
  busy: CardRedrawBusy;
  redraw: (fieldName: string) => void;
  apply: () => void;
  discard: () => void;
  /** Read the art again (after the cards changed). */
  reload: () => void;
}

/**
 * Redraw one image of the brand's Casino and Sports cards (Above the fold), through spark-creatives' per-image redraw
 * of its website art: casino-creation draws a candidate for that one image of the brand's hero deployment, charged the
 * per-image price, and nothing changes on the site until the owner applies it. The art is read only while `enabled`
 * (the Above the fold tab shows the brand's own cards): it reads every deployment from casino-creation.
 *
 * A requested redraw is followed through the jobs feed (the websocket and the polled list) like the both-cards run; a
 * redraw waiting from an earlier visit is found again through the hero's `pending_redraw_id`. A 402 opens Buy credits
 * with what is missing (`onNeedCredits`); refusals and outcomes are told through `onMessage`; an applied candidate
 * re-reads the cards (`onApplied`).
 */
export const useAtfCardRedraws = (opts: {
  tenantId: number | undefined;
  enabled: boolean;
  /** A both-cards regeneration is in flight: its art would make a candidate stale, so no redraw is asked for. */
  cardsRunning: boolean;
  /** The cards record's images by name (what the site shows): they tell which hero the cards come from (atfHeroOf). */
  liveImages?: Record<string, string>;
  onApplied: () => void;
  onMessage: (message: string) => void;
  onNeedCredits: (needed?: number) => void;
}): AtfCardRedrawsState => {
  const { tenantId, enabled, cardsRunning, liveImages, onApplied, onMessage, onNeedCredits } = opts;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const jobs = useAppSelector(selectCreativeJobsMap);

  const [attempt, setAttempt] = React.useState(0);
  const [last, setLast] = React.useState<{ key: string; tenantId: number; read: SiteArtRead } | null>(null);
  const [held, setHeld] = React.useState<{ tenantId: number; redraw: SiteRedraw } | null>(null);
  const [busy, setBusy] = React.useState<CardRedrawBusy>(null);

  const asked = enabled && tenantId !== undefined && creativesServiceConfigured();
  const key = `${tenantId ?? ''}|${attempt}`;
  const reload = React.useCallback(() => setAttempt((n) => n + 1), []);

  // The callbacks change identity with the page's renders; the async paths read the latest through a ref.
  const handlers = React.useRef({ onApplied, onMessage, onNeedCredits });
  React.useEffect(() => {
    handlers.current = { onApplied, onMessage, onNeedCredits };
  }, [onApplied, onMessage, onNeedCredits]);
  // The live card images as they are now, for a read landing later (it picks its hero with them).
  const liveNow = React.useRef(liveImages);
  liveNow.current = liveImages;
  // The brand the page shows now: an answer for another brand is not acted on.
  const tenantNow = React.useRef(tenantId);
  React.useEffect(() => {
    tenantNow.current = tenantId;
  }, [tenantId]);

  // A logical clock: a read adopts the hero's pending redraw only when it started after the last change made here, so
  // a read that left before a redraw was queued (or applied) never undoes it when it lands.
  const clock = React.useRef(0);
  const changedAt = React.useRef(0);
  const hold = React.useCallback((tenant: number, redraw: SiteRedraw | null): void => {
    clock.current += 1;
    changedAt.current = clock.current;
    setHeld(redraw && isPendingRedraw(redraw) ? { tenantId: tenant, redraw } : null);
  }, []);

  // The read this hook last landed well (by key), so reopening the tab does not read every deployment again; a read
  // that failed or was refused is tried again the next time the tab opens.
  const readKey = React.useRef('');
  React.useEffect(() => {
    // Syncs with spark-creatives: reads the brand's website art, then the hero's waiting redraw, aborting a read a
    // newer brand or retry has replaced (or the tab closing) so its late answer never lands.
    if (!asked || tenantId === undefined || readKey.current === key) return undefined;
    const abort = new AbortController();
    clock.current += 1;
    const startedAt = clock.current;
    void (async () => {
      const read = await fetchSiteArt(tenantId, abort.signal);
      if (abort.signal.aborted || (!read.ok && read.aborted)) return;
      setLast({ key, tenantId, read });
      if (!read.ok) return;
      readKey.current = key;
      const pendingId = atfHeroOf(read.value, liveNow.current)?.pendingRedrawId ?? '';
      if (!pendingId) {
        if (startedAt > changedAt.current) setHeld(null);
        return;
      }
      const got = await fetchSiteRedraw(tenantId, pendingId, abort.signal);
      if (abort.signal.aborted || startedAt <= changedAt.current || !got.ok) return;
      setHeld(isPendingRedraw(got.redraw) ? { tenantId, redraw: got.redraw } : null);
    })();
    return () => abort.abort();
  }, [asked, tenantId, key]);

  // A both-cards regeneration that ended redrew (or left) the hero's images: read them again for their new ids.
  const wasRunning = React.useRef(cardsRunning);
  React.useEffect(() => {
    if (wasRunning.current && !cardsRunning) reload();
    wasRunning.current = cardsRunning;
  }, [cardsRunning, reload]);

  // The last good art of this brand stays on screen while it is read again.
  const art = asked && last && last.tenantId === tenantId && last.read.ok ? last.read.value : null;
  const hero = React.useMemo(() => (art ? atfHeroOf(art, liveImages) : null), [art, liveImages]);
  const offers = React.useMemo(() => (art?.redrawPriced && hero ? cardRedrawOffers(hero) : null), [art, hero]);
  const pending = held && held.tenantId === tenantId ? held.redraw : null;

  const drawing = pending?.status === 'drawing' ? pending : null;
  const job = drawing?.jobId ? jobs[drawing.jobId] : undefined;
  React.useEffect(() => {
    // Syncs with the jobs feed: follows the waiting redraw's draw to its end. A redraw found from an earlier visit
    // whose job the feed does not hold yet is put in it as running, so the feed's own re-reads follow it.
    if (!drawing || tenantId === undefined) return;
    if (!job) {
      if (drawing.jobId) {
        dispatch(
          upsertJob({
            id: drawing.jobId,
            tenant_id: String(tenantId),
            kind: SITE_REDRAW_KIND,
            placement: SITE_REDRAW_PLACEMENT,
            root_banner_id: drawing.deploymentUuid,
            parent_id: drawing.id,
            status: 'running',
          }),
        );
      }
      return;
    }
    if (job.status === 'done') {
      if (job.result_url) {
        hold(tenantId, { ...drawing, status: 'ready', candidateUrl: job.result_url });
        return;
      }
      void fetchSiteRedraw(tenantId, drawing.id).then((got) => {
        if (got.ok && tenantNow.current === tenantId) hold(tenantId, got.redraw);
      });
      return;
    }
    if (job.status === 'failed') {
      hold(tenantId, null);
      // A candidate the owner threw away while it was drawn (elsewhere) is no news.
      if (job.error !== 'site_redraw_discarded') handlers.current.onMessage(t("The new image couldn't be drawn. Your credits were refunded."));
    }
  }, [drawing, job, tenantId, dispatch, hold, t]);

  const redraw = React.useCallback(
    (fieldName: string): void => {
      const offer = offers?.[fieldName];
      if (tenantId === undefined || !hero || offer?.kind !== 'redrawable' || pending || busy || cardsRunning) return;
      const tenant = tenantId;
      const deploymentUuid = hero.deploymentUuid;
      setBusy({ action: 'redraw', fieldName });
      void requestSiteRedraw(tenant, deploymentUuid, offer.assetId).then((res) => {
        setBusy(null);
        if (tenantNow.current !== tenant) return;
        const h = handlers.current;
        if (res.ok) {
          hold(tenant, res.redraw);
          if (res.redraw.jobId) {
            dispatch(
              upsertJob({
                id: res.redraw.jobId,
                tenant_id: String(tenant),
                kind: SITE_REDRAW_KIND,
                placement: SITE_REDRAW_PLACEMENT,
                root_banner_id: res.redraw.deploymentUuid || deploymentUuid,
                parent_id: res.redraw.id,
                status: 'queued',
              }),
            );
          }
          return;
        }
        switch (redrawRefusal(res.status, res.error)) {
          case 'credits':
            h.onNeedCredits(res.needed);
            return;
          case 'pending':
            // The art read here was older than the waiting redraw: read it again to show that one.
            h.onMessage(t('Finish or discard the waiting redraw first.'));
            reload();
            return;
          case 'site-busy':
            h.onMessage(t('Your website art is being regenerated. Try again once that is done.'));
            return;
          case 'not-redrawable':
            h.onMessage(t("This image can't be redrawn right now."));
            reload();
            return;
          case 'refunded':
            h.onMessage(t("The new image couldn't be drawn. Your credits were refunded."));
            return;
          case 'unavailable':
            h.onMessage(t("Redrawing images isn't available right now."));
            return;
          case 'busy':
            h.onMessage(t('Your site is busy right now. Please try again shortly.'));
            return;
          default:
            h.onMessage(t("The image couldn't be redrawn. Please try again."));
        }
      });
    },
    [offers, tenantId, hero, pending, busy, cardsRunning, hold, dispatch, reload, t],
  );

  const apply = React.useCallback((): void => {
    if (tenantId === undefined || pending?.status !== 'ready' || busy) return;
    const tenant = tenantId;
    const id = pending.id;
    setBusy({ action: 'apply' });
    void (async () => {
      const res = await applySiteRedraw(tenant, id);
      if (tenantNow.current !== tenant) {
        setBusy(null);
        return;
      }
      const h = handlers.current;
      if (res.ok) {
        setBusy(null);
        hold(tenant, res.redraw);
        if (res.redraw.status === 'applied') {
          h.onMessage(t('The new image is live on your site.'));
          h.onApplied();
          // casino-creation may give the hero's images new ids after an apply.
          reload();
        }
        return;
      }
      const why = applyRefusal(res.status, res.error);
      if (why === 'apply-failed') {
        // The redraw says whether its candidate is kept (ready: try again) or lost (failed: refunded).
        const again = await fetchSiteRedraw(tenant, id);
        setBusy(null);
        if (tenantNow.current !== tenant) return;
        if (again.ok && !isPendingRedraw(again.redraw)) {
          hold(tenant, again.redraw);
          h.onMessage(
            again.redraw.refunded
              ? t("The new image couldn't be applied. Your credits were refunded.")
              : t("The new image couldn't be applied."),
          );
          return;
        }
        h.onMessage(t("The new image couldn't be applied. Please try again."));
        return;
      }
      setBusy(null);
      switch (why) {
        case 'stale':
          hold(tenant, null);
          reload();
          h.onMessage(t("Your site changed since this image was drawn, so it can't be applied. Your credits were refunded."));
          return;
        case 'expired':
          hold(tenant, null);
          reload();
          h.onMessage(t('This new image expired before it was applied.'));
          return;
        case 'gone':
          hold(tenant, null);
          reload();
          h.onMessage(t("This new image can't be applied anymore."));
          return;
        case 'busy':
          h.onMessage(t('Your site is busy right now. Please try again shortly.'));
          return;
        case 'unavailable':
          h.onMessage(t("Applying images isn't available right now. Please try again later."));
          return;
        default:
          h.onMessage(t("The new image couldn't be applied. Please try again."));
      }
    })();
  }, [tenantId, pending, busy, hold, reload, t]);

  const discard = React.useCallback((): void => {
    if (tenantId === undefined || pending?.status !== 'ready' || busy) return;
    const tenant = tenantId;
    const id = pending.id;
    setBusy({ action: 'discard' });
    void discardSiteRedraw(tenant, id).then((res) => {
      setBusy(null);
      if (tenantNow.current !== tenant) return;
      const h = handlers.current;
      if (res.ok) {
        hold(tenant, res.redraw);
        return;
      }
      switch (discardRefusal(res.status, res.error)) {
        case 'applied':
          hold(tenant, null);
          h.onMessage(t('This new image is already live on your site.'));
          h.onApplied();
          reload();
          return;
        case 'gone':
          hold(tenant, null);
          reload();
          return;
        case 'busy':
          h.onMessage(t('Your site is busy right now. Please try again shortly.'));
          return;
        default:
          h.onMessage(t("The new image couldn't be discarded. Please try again."));
      }
    });
  }, [tenantId, pending, busy, hold, reload, t]);

  return {
    offers,
    price: art?.redrawPriced ? art.redrawPrice : undefined,
    pending,
    busy,
    redraw,
    apply,
    discard,
    reload,
  };
};
